import React, { useState, useEffect } from 'react';
import { testFCMNotification } from '../config/firebase';
import { Smartphone, Monitor as MonitorIcon, Search, RefreshCw, Trash2, Shield, Activity } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { motion, AnimatePresence } from 'framer-motion';

interface Device {
  device_name: string;
  platform: string;
  browser: string;
  os: string;
  registered_at: string;
  last_used: string;
  fcm_token: string;
}

export const DeviceManager: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [testingFCM, setTestingFCM] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await fetch('/api/users/devices', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDevices(data.devices || []);
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeDevice = async (fcmToken: string) => {
    try {
      const response = await fetch(`/api/users/devices/${fcmToken}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('csense_auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchDevices(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to remove device:', error);
    }
  };

  const handleTestFCM = async () => {
    setTestingFCM(true);
    try {
      await testFCMNotification('🧪 Test notification from Neural Core');
    } catch (error) {
      console.error('FCM test failed:', error);
    } finally {
      setTestingFCM(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return 'Unknown';
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'web': return MonitorIcon;
      default: return Smartphone;
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl animate-pulse">
         <div className="h-6 w-48 bg-white/10 rounded mb-6"></div>
         <div className="space-y-4">
            <div className="h-24 bg-white/5 rounded-xl"></div>
            <div className="h-24 bg-white/5 rounded-xl"></div>
         </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 backdrop-blur-xl shadow-2xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
           <h3 className="text-sm font-black text-white uppercase tracking-widest flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-400" />
              Authenticated Nodes
           </h3>
           <p className="text-[10px] text-blue-100/40 uppercase tracking-widest mt-1">
              Active push notification targets
           </p>
        </div>
        <Button
          onClick={handleTestFCM}
          disabled={testingFCM}
          className={`font-black uppercase text-[10px] tracking-widest px-6 py-4 rounded-xl transition-all ${
            testingFCM
              ? 'bg-blue-600/20 text-blue-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)]'
          }`}
        >
          {testingFCM ? <><RefreshCw className="w-3.5 h-3.5 mr-2 animate-spin" /> SYNCHRONIZING</> : <><Activity className="w-3.5 h-3.5 mr-2" /> PING ALL NODES</>}
        </Button>
      </div>

      {devices.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-white/5 rounded-xl bg-white/[0.02]">
          <Search className="w-10 h-10 text-blue-500/20 mx-auto mb-4" />
          <p className="text-xs font-black text-blue-100/40 uppercase tracking-widest mb-1">No Active Nodes Detected</p>
          <p className="text-[10px] text-blue-100/20">Devices will register upon authentication</p>
        </div>
      ) : (
        <div className="space-y-4">
          <AnimatePresence>
            {devices.map((device, index) => {
              const Icon = getPlatformIcon(device.platform);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  key={device.fcm_token + index}
                  className="p-5 rounded-xl bg-black/40 border border-white/5 hover:border-blue-500/30 transition-all group relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-6 relative z-10">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                         <Icon className="w-5 h-5 text-blue-400" />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                           <h4 className="font-bold text-white uppercase text-xs">{device.device_name}</h4>
                           <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[9px] font-black uppercase tracking-widest px-2 py-0">ACTIVE</Badge>
                        </div>
                        
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] text-blue-100/40 font-mono">
                          <p><span className="uppercase text-blue-100/20 tracking-widest mr-1">OS:</span> {device.os}</p>
                          <p><span className="uppercase text-blue-100/20 tracking-widest mr-1">Portal:</span> {device.browser}</p>
                          <p><span className="uppercase text-blue-100/20 tracking-widest mr-1">Link est:</span> {formatDate(device.registered_at)}</p>
                          <p className="text-emerald-400/60"><span className="uppercase text-emerald-400/30 tracking-widest mr-1">Ping:</span> {formatDate(device.last_used)}</p>
                        </div>
                      </div>
                    </div>
                    
                    <Button
                      onClick={() => removeDevice(device.fcm_token)}
                      variant="ghost"
                      className="w-full sm:w-auto px-4 py-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-all font-black text-[10px] uppercase tracking-widest border border-red-500/20"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Revoke
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          
          <div className="flex items-center gap-3 p-4 bg-blue-900/10 rounded-xl border border-blue-500/10 text-[10px] font-light text-blue-100/60 italic mt-6">
            <Shield className="w-4 h-4 text-blue-400/60 flex-shrink-0" />
            <p>Revoking access will immediately terminate the push notification link to that hardware node. To re-establish, the user must re-authenticate on the device.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManager;